import { lazy, Suspense, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { resolveStorageUrl } from '@/lib/firebase-storage-service';
import { useQuestionList } from '@/modules/sinarem-reports/presentation/hooks/use-question-list';
import type { QuestionListItem } from '@/modules/sinarem-reports/domain/entities/question-list.entity';
import { Section, areaColor } from './shared';
import {
  DIFFICULTY_TONE,
  difficultyLabel,
  optionsFromQuestion,
} from './utils/question-view-model';

// Enunciado/comentário vêm em markdown com imagens referenciadas por path do
// Firebase Storage (ex.: "images/questions/x_body.png"), não por URL — resolve
// via resolveStorageUrl antes de renderizar.
function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const [url, setUrl] = useState<string | null>(
    src && /^https?:\/\//.test(src) ? src : null,
  );

  useEffect(() => {
    if (!src || /^https?:\/\//.test(src)) return;
    let cancelled = false;
    resolveStorageUrl(src)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch((error: unknown) => {
        console.error('Falha ao carregar imagem da questão', src, error);
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (!url) {
    return (
      <span className="bg-(--color-border)/40 my-2 block h-40 w-full animate-pulse rounded-lg" />
    );
  }

  return (
    <img
      src={url}
      alt={alt ?? ''}
      className="border-(--color-border) my-2 max-h-96 w-auto max-w-full rounded-lg border object-contain"
    />
  );
}

const QUESTION_MARKDOWN_COMPONENTS: Components = {
  img: MarkdownImage,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => (
    <strong className="text-(--color-text-primary) font-semibold">
      {children}
    </strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-(--color-accent) underline underline-offset-2"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="bg-(--color-border)/60 rounded px-1 py-0.5 font-mono text-[11px]">
      {children}
    </code>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-(--color-accent)/50 mb-2 border-l-2 pl-3 italic last:mb-0">
      {children}
    </blockquote>
  ),
};

function QuestionMarkdown({ content }: { content: string }) {
  return (
    <Markdown components={QUESTION_MARKDOWN_COMPONENTS}>{content}</Markdown>
  );
}

// @react-pdf/renderer é pesado (~1.3 MB) — só carrega quando a aba Questões
// realmente abre, em vez de inflar o bundle principal do dashboard.
const DownloadExamButton = lazy(() => import('./pdf/exam-pdf'));

function DifficultyBadge({
  difficulty,
  suffix,
}: {
  difficulty?: string;
  suffix?: string;
}) {
  const tone = difficulty ? DIFFICULTY_TONE[difficulty] : undefined;
  const classes =
    tone === 'easy'
      ? 'bg-emerald-500/15 text-emerald-400'
      : tone === 'hard'
        ? 'bg-red-500/15 text-red-400'
        : tone === 'medium'
          ? 'bg-amber-500/15 text-amber-400'
          : 'bg-(--color-border) text-(--color-text-secondary)';

  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${classes}`}
    >
      {difficultyLabel(difficulty)}
      {suffix}
    </span>
  );
}

function QuestionResultIcon({ isCorrect }: { isCorrect: boolean }) {
  return (
    <span
      className={[
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
        isCorrect
          ? 'bg-emerald-500/20 text-emerald-400'
          : 'bg-red-500/20 text-red-400',
      ].join(' ')}
    >
      <svg
        className="h-3 w-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={3}
      >
        {isCorrect ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 12.75 6 6 9-13.5"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18 18 6M6 6l12 12"
          />
        )}
      </svg>
    </span>
  );
}

function QuestionOptionRow({
  option,
}: {
  option: ReturnType<typeof optionsFromQuestion>[number];
}) {
  const classes = option.isCorrect
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
    : 'border-(--color-border) bg-(--color-bg-primary)/40 text-(--color-text-secondary)';

  const fillColor = option.isCorrect
    ? 'bg-emerald-500/15'
    : 'bg-(--color-border)/40';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border px-4 py-2.5 text-sm ${classes}`}
    >
      <div
        className={`absolute inset-y-0 left-0 ${fillColor}`}
        style={{ width: `${option.percent}%` }}
        aria-hidden
      />
      <div className="relative flex items-center gap-3">
        <span
          className={[
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            option.isCorrect ? 'bg-white/10' : 'bg-(--color-border)',
          ].join(' ')}
        >
          {option.id}
        </span>
        <p className="flex-1">{option.text}</p>
        <span className="shrink-0 text-xs font-bold tabular-nums opacity-80">
          {option.percent}%
        </span>
        {option.isCorrect && <QuestionResultIcon isCorrect />}
      </div>
    </div>
  );
}

function InstitutionResultSummary({
  correctCount,
  wrongCount,
  notAnsweredCount,
}: {
  correctCount: number;
  wrongCount: number;
  notAnsweredCount: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:gap-2.5">
      <span className="text-(--color-text-secondary) text-[10px] font-semibold uppercase tracking-wide">
        Seus alunos:
      </span>
      <span className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-400">
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 12.75 6 6 9-13.5"
          />
        </svg>
        {correctCount} acertaram
      </span>
      <span className="flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-bold text-red-400">
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18 18 6M6 6l12 12"
          />
        </svg>
        {wrongCount} erraram
      </span>
      <span className="flex items-center gap-1 rounded-full bg-slate-500/15 px-2 py-0.5 text-xs font-bold text-slate-400">
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
        </svg>
        {notAnsweredCount} não fizeram
      </span>
    </div>
  );
}

function QuestionAreaBadge({
  area,
  specialty,
  theme,
}: {
  area: string;
  specialty: string;
  theme: string;
}) {
  const color = areaColor(area);
  const label = [area, specialty, theme].filter(Boolean).join(' · ');

  return (
    <span
      className="inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ backgroundColor: `${color}26`, color }}
    >
      {label}
    </span>
  );
}

function QuestionCard({
  index,
  question,
}: {
  index: number;
  question: QuestionListItem;
}) {
  const [showComment, setShowComment] = useState(false);
  const options = optionsFromQuestion(question);

  return (
    <div className="space-y-3 px-6 py-5">
      <div className="flex flex-wrap items-center gap-2.5 py-2">
        <p className="text-(--color-text-primary) text-sm font-bold">
          Questão {index}
        </p>
        <span className="border-(--color-border) text-(--color-text-secondary) rounded-full border px-2.5 py-0.5 text-[10px] font-medium">
          {question.institution} · {question.state} · {question.year}
        </span>
        <DifficultyBadge difficulty={question.difficulty} />
        <DifficultyBadge difficulty={question.triDifficulty} suffix=" - TRI" />
        <InstitutionResultSummary
          correctCount={question.totalCorrect}
          wrongCount={question.totalWrong}
          notAnsweredCount={question.totalNotAnswered}
        />
      </div>
      <QuestionAreaBadge
        area={question.area}
        specialty={question.specialty}
        theme={question.theme}
      />
      <div className="text-(--color-text-secondary) text-sm leading-relaxed">
        <QuestionMarkdown content={question.body} />
      </div>
      <div className="space-y-2">
        {options.map((option) => (
          <QuestionOptionRow key={option.id} option={option} />
        ))}
      </div>
      {question.officialComment && (
        <>
          <button
            type="button"
            onClick={() => setShowComment((v) => !v)}
            className="text-(--color-accent) flex items-center gap-1.5 text-xs font-semibold"
          >
            <svg
              className={`h-3.5 w-3.5 transition-transform ${showComment ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m19 9-7 7-7-7"
              />
            </svg>
            Ver comentário Aristo
          </button>
          {showComment && (
            <div className="border-(--color-border) bg-(--color-bg-primary)/40 text-(--color-text-secondary) rounded-xl border px-4 py-3 text-xs leading-relaxed">
              <QuestionMarkdown content={question.officialComment} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

type QuestionRankTab = 'erros' | 'acertos';

function sortQuestions(
  questions: QuestionListItem[],
  tab: QuestionRankTab,
): QuestionListItem[] {
  return [...questions].sort((a, b) =>
    tab === 'erros'
      ? b.totalWrong - a.totalWrong
      : b.totalCorrect - a.totalCorrect,
  );
}

function TopQuestionsSection({ questions }: { questions: QuestionListItem[] }) {
  const [tab, setTab] = useState<QuestionRankTab>('erros');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sorted = sortQuestions(questions, tab).slice(0, 10);
  const selected =
    sorted.find((q) => q.questionId === selectedId) ?? sorted[0] ?? null;
  const selectedIndex = selected
    ? sorted.findIndex((q) => q.questionId === selected.questionId) + 1
    : 0;

  const handleTabChange = (next: QuestionRankTab) => {
    setTab(next);
    setSelectedId(null);
  };

  if (!selected) return null;

  return (
    <Section title="Ranking de questões">
      <div
        className={[
          'bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border-2 backdrop-blur-sm',
          tab === 'erros' ? 'border-red-500/40' : 'border-emerald-500/40',
        ].join(' ')}
      >
        <div className="border-(--color-border) flex gap-1.5 border-b px-6 py-4">
          {(
            [
              { id: 'erros', label: 'Top 10 erros dos seus alunos' },
              { id: 'acertos', label: 'Top 10 acertos dos seus alunos' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleTabChange(opt.id)}
              className={[
                'flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all',
                tab === opt.id
                  ? 'border-(--color-accent) bg-(--color-accent)/10 text-(--color-accent)'
                  : 'border-(--color-border) text-(--color-text-secondary) hover:border-(--color-accent)/50 hover:text-(--color-text-primary) bg-(--color-bg-primary)/40',
              ].join(' ')}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="border-(--color-border) border-b px-6 py-4">
          <div className="flex flex-wrap gap-2">
            {sorted.map((q, i) => (
              <button
                key={q.questionId}
                type="button"
                onClick={() => setSelectedId(q.questionId)}
                className={[
                  'rounded-lg px-3.5 py-2 text-sm font-bold transition-colors',
                  q.questionId === selected.questionId
                    ? 'bg-(--color-accent) text-(--color-bg-primary)'
                    : 'bg-(--color-accent)/10 text-(--color-text-secondary) hover:bg-(--color-accent)/20',
                ].join(' ')}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
        <QuestionCard index={selectedIndex} question={selected} />
      </div>
    </Section>
  );
}

export function QuestionsTab({
  editionId,
  editionTitle,
}: {
  editionId: string;
  editionTitle: string;
}) {
  const { data, isLoading } = useQuestionList(editionId);
  const questions = data?.questions ?? [];

  if (isLoading) {
    return <div className="h-64 animate-pulse rounded-2xl bg-white/5" />;
  }

  if (!questions.length) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 rounded-2xl border px-6 py-10 text-center backdrop-blur-sm">
        <p className="text-(--color-text-secondary) text-sm">
          Nenhuma questão encontrada para esta edição.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Suspense
          fallback={
            <span className="border-(--color-border) text-(--color-text-secondary) inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold opacity-60">
              Baixar Exame
            </span>
          }
        >
          <DownloadExamButton
            questions={questions}
            editionTitle={editionTitle}
          />
        </Suspense>
      </div>
      <TopQuestionsSection questions={questions} />
      <Section title="Questões respondidas">
        <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
          <div className="border-(--color-border) border-b px-6 py-4">
            <p className="text-(--color-text-primary) text-base font-bold">
              Questões Respondidas
            </p>
            <p className="text-(--color-text-secondary) mt-0.5 text-xs">
              {questions.length} questões
            </p>
          </div>
          <div className="divide-(--color-border) divide-y">
            {questions.map((question, i) => (
              <QuestionCard
                key={question.questionId}
                index={i + 1}
                question={question}
              />
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}
